/**
 * Microsoft Azure Translator Service
 * Handles translation requests using Azure Translator API
 */

class AzureTranslatorService {
  constructor() {
    // Get keys from environment variables
    this.subscriptionKey = process.env.REACT_APP_AZURE_TRANSLATOR_KEY_1 || 
                          process.env.REACT_APP_AZURE_TRANSLATOR_KEY_2;
    this.endpoint = 'https://api.cognitive.microsofttranslator.com';
    this.location = process.env.REACT_APP_AZURE_TRANSLATOR_LOCATION || 'centralus';
    this.translationCache = new Map();
    
    if (this.subscriptionKey) {
      console.log('[Azure Translator] ✅ Service initialized');
    } else {
      console.warn('[Azure Translator] ⚠️ No API key found. Add REACT_APP_AZURE_TRANSLATOR_KEY_1 to .env');
    }
  }

  getCacheKey(text, targetLanguage, sourceLanguage = 'en') {
    return `${sourceLanguage}-${targetLanguage}-${text}`;
  }

  async translate(text, targetLanguage, sourceLanguage = 'en') {
    if (sourceLanguage === targetLanguage || !text || text.trim() === '') {
      return text;
    }

    if (sourceLanguage !== 'auto') {
      const cacheKey = this.getCacheKey(text, targetLanguage, sourceLanguage);
      if (this.translationCache.has(cacheKey)) {
        return this.translationCache.get(cacheKey);
      }
    }

    if (!this.subscriptionKey) {
      return text;
    }

    try {
      const fromParam = sourceLanguage === 'auto' ? '' : `&from=${sourceLanguage}`;
      const response = await fetch(
        `${this.endpoint}/translate?api-version=3.0${fromParam}&to=${targetLanguage}`,
        {
          method: 'POST',
          headers: {
            'Ocp-Apim-Subscription-Key': this.subscriptionKey,
            'Ocp-Apim-Subscription-Region': this.location,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify([{ text }]),
        }
      );

      if (!response.ok) {
        if (this.subscriptionKey === process.env.REACT_APP_AZURE_TRANSLATOR_KEY_1 && 
            process.env.REACT_APP_AZURE_TRANSLATOR_KEY_2) {
          this.subscriptionKey = process.env.REACT_APP_AZURE_TRANSLATOR_KEY_2;
          return this.translate(text, targetLanguage, sourceLanguage);
        }
        throw new Error(`Translation failed: ${response.status}`);
      }

      const result = await response.json();
      const translatedText = result[0]?.translations[0]?.text || text;

      if (sourceLanguage !== 'auto') {
        const cacheKey = this.getCacheKey(text, targetLanguage, sourceLanguage);
        this.translationCache.set(cacheKey, translatedText);
      }

      return translatedText;
    } catch (error) {
      console.error('[Azure Translator] Error:', error);
      return text;
    }
  }

  async translateBatch(texts, targetLanguage, sourceLanguage = 'en') {
    if (sourceLanguage === targetLanguage) {
      return texts;
    }

    const results = [];
    const textsToTranslate = [];
    const indices = [];

    texts.forEach((text, index) => {
      if (!text || text.trim() === '') {
        results[index] = text;
      } else {
        if (sourceLanguage !== 'auto') {
          const cacheKey = this.getCacheKey(text, targetLanguage, sourceLanguage);
          if (this.translationCache.has(cacheKey)) {
            results[index] = this.translationCache.get(cacheKey);
            return;
          }
        }
        textsToTranslate.push(text);
        indices.push(index);
      }
    });

    if (textsToTranslate.length === 0) {
      return results;
    }

    if (!this.subscriptionKey) {
      return texts;
    }

    try {
      const fromParam = sourceLanguage === 'auto' ? '' : `&from=${sourceLanguage}`;
      const response = await fetch(
        `${this.endpoint}/translate?api-version=3.0${fromParam}&to=${targetLanguage}`,
        {
          method: 'POST',
          headers: {
            'Ocp-Apim-Subscription-Key': this.subscriptionKey,
            'Ocp-Apim-Subscription-Region': this.location,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(textsToTranslate.map(text => ({ text }))),
        }
      );

      if (!response.ok) {
        if (this.subscriptionKey === process.env.REACT_APP_AZURE_TRANSLATOR_KEY_1 && 
            process.env.REACT_APP_AZURE_TRANSLATOR_KEY_2) {
          this.subscriptionKey = process.env.REACT_APP_AZURE_TRANSLATOR_KEY_2;
          return this.translateBatch(texts, targetLanguage, sourceLanguage);
        }
        throw new Error(`Batch translation failed: ${response.status}`);
      }

      const result = await response.json();
      
      result.forEach((item, idx) => {
        const originalIndex = indices[idx];
        const translatedText = item.translations[0]?.text || textsToTranslate[idx];
        results[originalIndex] = translatedText;
        
        if (sourceLanguage !== 'auto') {
          const cacheKey = this.getCacheKey(textsToTranslate[idx], targetLanguage, sourceLanguage);
          this.translationCache.set(cacheKey, translatedText);
        }
      });

      return results;
    } catch (error) {
      console.error('[Azure Translator] Batch error:', error);
      return texts;
    }
  }

  clearCache() {
    this.translationCache.clear();
  }
}

const azureTranslatorService = new AzureTranslatorService();
export default azureTranslatorService;













